import React, { useEffect, useState } from "react";
import { api, coverUrl } from "@/lib/api";
import { TrackCollection } from "@/components/TrackCollection";
import { Play, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlayer } from "@/context/PlayerContext";
import PackShowcase from "@/components/PackShowcase";
import { PageLoading, LoadError } from "@/components/LoadState";

export default function Home({ config }) {
  const [latest, setLatest] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [top, setTop] = useState([]);
  const [newPacks, setNewPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const p = usePlayer();

  useEffect(() => {
    (async () => {
      setLoading(true); setError(false);
      try {
        const [latestRes, topRes, featRes, packsRes] = await Promise.all([
          api.get("/tracks/latest?limit=64"),
          api.get("/tracks?top=true&limit=12"),
          api.get("/tracks?featured=true&limit=1"),
          api.get("/packs?new=true").catch(() => ({ data: [] })),
        ]);
        setLatest(latestRes.data);
        setNewPacks(packsRes.data);
        setTop(topRes.data);
        setFeatured(featRes.data[0] || latestRes.data[0] || null);
      } catch { setError(true); }
      setLoading(false);
    })();
  }, [attempt]);

  return (
    <div>
      {/* Panoramic Banner just below menu */}
      <section className="relative w-full h-[38vh] sm:h-[46vh] lg:h-[56vh] overflow-hidden border-b border-white/5" data-testid="hero-banner">
        {config?.banner_path ? (
          <img
            src={coverUrl(config.banner_path, 0)}
            alt="POMATA banner"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_music-vault-345/artifacts/799wfzrp_pomata%20medusa.webp"
            alt="banner"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 pb-8 sm:pb-14">
            <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.9] uppercase tracking-tight text-white max-w-4xl">
              POMATA <span className="text-white/60 font-mono text-base sm:text-lg tracking-[0.4em] block sm:inline-block sm:ml-4">In Session</span>
            </h1>
            <p className="mt-4 text-white/70 max-w-xl text-sm sm:text-base">
              {config?.tagline || "Remixes · Mashups · Packs — Hecho para la pista."}
            </p>
            <div className="mt-6 flex gap-3">
              {featured && (
                <button data-testid="hero-featured-play-button"
                  onClick={() => p.playTrack(featured, latest)}
                  className="flex items-center gap-2 btn-red px-6 py-3 rounded-full font-semibold text-white">
                  <Play className="w-4 h-4" /> Reproducir destacado
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* LATEST */}
      <Section title="Novedades" subtitle="Lo último de POMATA" cta={{ to: "/pool", label: "Ver todo" }}>
        {loading ? <PageLoading rows={4} testid="home-loading" /> : error ? <LoadError onRetry={() => setAttempt((a) => a + 1)} testid="home-error" /> : (<>
        {newPacks.length > 0 && (
          <div className="space-y-6 mb-10" data-testid="home-new-packs">
            {newPacks.slice(0, 3).map((pk) => <PackShowcase key={pk.id} pack={pk} config={config} />)}
          </div>
        )}
        {latest.length === 0 && newPacks.length === 0 ? <Empty text="Todavía no hay novedades marcadas. Marca la casilla «Novedad» al subir una pista o un pack." /> : latest.length > 0 && (
          <TrackCollection tracks={latest} config={config} testid="home-latest" />
        )}
        </>)}
      </Section>


      {top.length > 0 && (
        <Section title="Lo más top" subtitle="Los imprescindibles de POMATA">
          <TrackCollection tracks={top} config={config} testid="home-top-grid" />
        </Section>
      )}


      {/* BIO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="home-bio">
        <div className="mb-8">
          <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-2">Biografía</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight text-white" data-testid="home-bio-title">Sobre mí</h2>
        </div>
        <div className="space-y-4 text-white/70 text-sm sm:text-base leading-relaxed max-w-3xl">
            <p>Samuel Pomata es un productor y DJ español de House y Tech House, conocido por crear temas cargados de groove, ritmos potentes y producciones enérgicas enfocadas al club. Su sonido combina percusiones contundentes, líneas de bajo contagiosas y estructuras pensadas para la pista de baile, creando música diseñada para los momentos de máxima energía.</p>
            <p>Con lanzamientos en sellos nacionales e internacionales, POMATA se ha ganado una reputación como productor de temas que conectan tanto con DJs como con el público de los clubs. Su música ha recibido el apoyo de emisoras de radio, playlists y artistas de la escena electrónica, acumulando además cientos de miles de reproducciones en plataformas digitales.</p>
            <p>En constante evolución de su sonido, pero manteniéndose fiel a sus raíces underground, POMATA continúa creando producciones enérgicas de House y Tech House con un claro enfoque en el groove, la atmósfera y el impacto en la pista de baile.</p>
        </div>
      </section>
    </div>
  );
}

function Section({ title, subtitle, cta, children }) {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-2">{subtitle}</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight text-white">
            {title}
          </h2>
        </div>
        {cta && (
          <Link to={cta.to} className="text-sm font-mono uppercase tracking-widest text-white/50 hover:text-red-500 flex items-center gap-1">
            {cta.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return (
    <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center text-white/40 text-sm">
      {text}
    </div>
  );
}
